import { Injectable, computed, signal } from '@angular/core';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import {
  DocumentProcessingStepEnum,
  isArchiveFile,
  isDocumentIndexationPending,
  LibrarySortField,
  ResponseProjectDocument,
  ResponseProjectUploadFile,
} from '../../models/response-project-document';
import { ResponseDocumentSubtype, SCOPE_INTERNAL_DOC, SUBTYPE_OTHER_NAME } from '../../models/response-document-subtype';
import { sortInternalDocumentSubtypes } from '../../components/documents/document-types';
import { ApiResponseDocumentsService } from '../api/response-documents/api-response-documents.service';
import { ApiResponseDocumentSubtypesService } from '../api/response-document-subtypes/api-response-document-subtypes.service';
import {
  MAX_ARCHIVE_SIZE_BYTES,
  MAX_FILE_SIZE_BYTES,
  UPLOAD_CONCURRENCY,
  WS_ERROR_REASON_MAP,
  WS_FALLBACK_ERROR_REASON,
  WS_REASON_CONFLICT_EXPIRED,
} from '../document-import.constants';
import { DocumentImportContext, DocumentImportService } from '../document-import.service';
import { TendersResponsesService, WsConnection } from '../tenders-responses.service';
import { DuplicateItem, DuplicateResolution } from '../../components/documents/dedup/dedup.model';
import { RejectedFile } from '../../models/rejected-file';
import { ResponseProjectProcessingPayload } from '../../models/response-project';

const SUBTYPE_COUNTS_DEBOUNCE_MS = 300;
const INDEXATION_POLL_INTERVAL_MS = 5000;
const INDEXATION_POLL_MAX_TICKS = 60;

export type LibraryImportPhase = 'idle' | 'uploading' | 'analyzing' | 'done';

export interface LibraryUploadItem {
  clientKey: string;
  uid: string;
  name: string;
  size?: number;
  documentId?: number;
  progress?: number;
  phase: 'uploading' | 'extracting' | 'analyzing' | 'done' | 'error';
  subtypeId?: number | null;
  customName?: string | null;
  uncategorized?: boolean;
  sourceFile?: File;
  replaceId?: number;
  isArchive?: boolean;
}

export function libraryUploadProcessingStep(upload: LibraryUploadItem): DocumentProcessingStepEnum | null {
  switch (upload.phase) {
    case 'uploading':
      return DocumentProcessingStepEnum.UPLOADING;
    case 'extracting':
      return DocumentProcessingStepEnum.EXTRACTING;
    case 'analyzing':
      return DocumentProcessingStepEnum.CATEGORIZING;
    case 'done':
      return upload.uncategorized ? DocumentProcessingStepEnum.UNCATEGORIZED : DocumentProcessingStepEnum.CATEGORIZED;
    default:
      return null;
  }
}

@Injectable({ providedIn: 'root' })
export class LibraryStateService {
  readonly documents = signal<ResponseProjectDocument[]>([]);
  /** Stable library size for the title pill (not the filtered page length). */
  readonly totalCount = signal(0);
  readonly query = signal('');
  readonly activeSubtypeIds = signal<number[]>([]);
  readonly sort = signal<LibrarySortField>('name');
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly hasMore = signal(false);
  readonly subtypes = signal<ResponseDocumentSubtype[]>([]);
  readonly subtypeCounts = signal<Record<number, number>>({});

  readonly importPhase = signal<LibraryImportPhase>('idle');
  readonly uploads = signal<LibraryUploadItem[]>([]);
  readonly duplicates = signal<DuplicateItem[]>([]);
  readonly rejected = signal<RejectedFile[]>([]);
  readonly pendingRefreshCount = signal(0);
  readonly replacedCount = signal(0);
  readonly showUncategorized = signal(false);
  /** Library-wide uncategorized docs (fetched — not derived from the paginated list). */
  readonly uncategorizedDocuments = signal<ResponseProjectDocument[]>([]);

  readonly hasPendingDuplicates = computed(() => this.duplicates().some(d => !d.resolution));
  readonly hasUncategorized = computed(() => this.uncategorizedDocuments().length > 0);
  readonly hasActiveImport = computed(() => this.importPhase() !== 'idle');
  readonly importDone = computed(() => {
    if (this.importPhase() !== 'done') return false;
    return !this.hasPendingDuplicates();
  });
  /** Phase shown in the progress card — derived from rows, not a parallel state machine. */
  readonly progressPhase = computed<'extracting' | 'uploading' | 'analyzing' | null>(() => {
    const list = this.uploads();
    if (list.some(u => u.phase === 'extracting')) return 'extracting';
    if (list.some(u => u.phase === 'uploading')) return 'uploading';
    if (list.some(u => u.phase === 'analyzing')) return 'analyzing';
    return null;
  });
  readonly uploadTotal = computed(() => this.uploads().length);
  readonly uploadCompleted = computed(() => this.uploads().filter(u => u.phase !== 'uploading').length);
  readonly analysisTotal = computed(() => this.uploads().filter(u => !u.isArchive).length);
  readonly analysisCompleted = computed(() => this.uploads().filter(u => u.phase === 'done' || u.phase === 'error').length);
  readonly batchProgressPercent = computed(() => {
    const phase = this.progressPhase();
    if (phase === 'uploading') {
      const total = this.uploadTotal();
      return total > 0 ? Math.min(100, (this.uploadCompleted() / total) * 100) : 0;
    }
    if (phase === 'analyzing' || phase === 'extracting') {
      const total = this.analysisTotal();
      return total > 0 ? Math.min(100, (this.analysisCompleted() / total) * 100) : 0;
    }
    return 0;
  });
  readonly showImportProgress = computed(() => this.progressPhase() != null);
  readonly filterSubtypes = computed(() =>
    sortInternalDocumentSubtypes(
      this.subtypes().filter(s => s.name !== 'undefined' && (s.name !== SUBTYPE_OTHER_NAME || (this.subtypeCounts()[s.id] ?? 0) > 0))
    )
  );
  readonly subtypeById = computed(() => {
    const map = new Map<number, ResponseDocumentSubtype>();
    for (const s of this.subtypes()) map.set(s.id, s);
    return map;
  });

  private page = 0;
  private tempCounter = 0;
  private duplicateCounter = 0;
  private defaultSubtypeId?: number;
  private activeWsConn: WsConnection | null = null;
  private wsSubs: Subscription[] = [];
  private loadSeq = 0;
  private readonly loadCancel$ = new Subject<void>();
  private readonly downloadInFlight = new Set<string>();
  private readonly libraryNameLookup = new Map<string, Promise<ResponseProjectDocument | undefined>>();

  private pendingWsConflicts = new Map<string, { name: string; resolution?: DuplicateResolution }>();
  private readonly silentReplaceUids = new Set<string>();
  private wsProcessingComplete = false;
  private subtypeCountsTimer: ReturnType<typeof setTimeout> | null = null;
  private indexationPollTimer: ReturnType<typeof setTimeout> | null = null;
  private indexationPollTicks = 0;
  private indexationPollEpoch = 0;
  /** Serializes import batches so a follow-up drop never wipes an in-flight one. */
  private importChain: Promise<void> = Promise.resolve();
  private importEpoch = 0;

  private readonly importContext: DocumentImportContext = {
    projectId: null,
    scope: SCOPE_INTERNAL_DOC,
  };

  constructor(
    private apiDocuments: ApiResponseDocumentsService,
    private apiSubtypes: ApiResponseDocumentSubtypesService,
    private documentImport: DocumentImportService,
    private tendersResponses: TendersResponsesService
  ) {}

  async init(): Promise<void> {
    if (!this.subtypes().length) await this.loadSubtypes();
    // Counts / uncategorized are independent of the list — don't block first paint.
    void this.loadSubtypeCounts();
    void this.loadUncategorized();
    await this.load(true, !this.documents().length);
  }

  async load(reset: boolean, showSkeleton = true): Promise<void> {
    if (reset) {
      this.page = 0;
      if (showSkeleton) this.loading.set(true);
      this.hasMore.set(false);
    } else {
      if (this.loadingMore() || !this.hasMore()) return;
      this.loadingMore.set(true);
    }

    this.loadCancel$.next();
    const seq = ++this.loadSeq;
    const q = this.query();
    const subtypeIds = this.activeSubtypeIds();
    const unfiltered = !q.trim() && !subtypeIds.length;
    const page = reset ? 0 : this.page + 1;
    try {
      const result = await firstValueFrom(
        this.apiDocuments.searchLibraryPage(q, subtypeIds, page, this.sort()).pipe(takeUntil(this.loadCancel$))
      );

      if (seq !== this.loadSeq) return;

      const rows = this.withoutInFlight(result.data);
      this.documents.update(prev => (reset ? rows : [...prev, ...rows]));
      this.page = page;
      const total = result.totalCount ?? 0;
      this.hasMore.set(this.documents().length < total);
      if (unfiltered) this.totalCount.set(total);
    } catch {
      if (seq !== this.loadSeq) return;
      // Keep the last successful page on error (avoid flashing an empty library).
      this.hasMore.set(false);
    } finally {
      if (seq === this.loadSeq) {
        this.loading.set(false);
        this.loadingMore.set(false);
        this.restartIndexationPolling();
      }
    }
  }

  /**
   * Indexation completes in a lambda that writes straight to the database, with no socket of its
   * own, so a row rendered as pending would otherwise stay that way until the next manual reload.
   * Polls while the freshly loaded list still holds pending rows, and stops on its own once none do.
   */
  private restartIndexationPolling(): void {
    this.stopIndexationPolling();
    if (!this.pendingIndexationIds().size) return;
    this.indexationPollTicks = 0;
    this.scheduleIndexationPoll(this.indexationPollEpoch);
  }

  stopIndexationPolling(): void {
    this.indexationPollEpoch++;
    if (!this.indexationPollTimer) return;
    clearTimeout(this.indexationPollTimer);
    this.indexationPollTimer = null;
  }

  private scheduleIndexationPoll(epoch: number): void {
    this.indexationPollTimer = setTimeout(() => {
      this.indexationPollTimer = null;
      void this.pollIndexation(epoch);
    }, INDEXATION_POLL_INTERVAL_MS);
  }

  private pendingIndexationIds(): Set<string> {
    return new Set(
      this.documents()
        .filter(doc => isDocumentIndexationPending(doc))
        .map(doc => doc.id)
    );
  }

  /**
   * One request per tick however many rows are pending, plus one more only on the tick where some
   * of them settle. The ids still pending come back as a set, and the rows that dropped out of it
   * are resolved together against the failed set rather than one lookup per document.
   */
  private async pollIndexation(epoch: number): Promise<void> {
    const tracked = this.pendingIndexationIds();
    if (!tracked.size) return;

    try {
      const pending = await firstValueFrom(this.apiDocuments.findPendingIndexationInLibrary());
      const stillPending = new Set(pending.data.map(doc => doc.id));
      const settled = [...tracked].filter(id => !stillPending.has(id));
      if (settled.length) await this.applySettledIndexation(settled);
    } catch {
      // Keep the statuses on screen; the next tick asks again.
    }

    if (epoch !== this.indexationPollEpoch) return;
    this.indexationPollTicks++;
    if (this.indexationPollTicks >= INDEXATION_POLL_MAX_TICKS) return;
    if (this.pendingIndexationIds().size) this.scheduleIndexationPoll(epoch);
  }

  /**
   * Settles the given rows against the failed set: absent from it means the indexation succeeded,
   * since the indexer never puts a document back to null. Keeps a failure distinguishable from a
   * success without assuming either, so the failure branches have real data to build on.
   */
  private async applySettledIndexation(ids: string[]): Promise<void> {
    const failed = await firstValueFrom(this.apiDocuments.findFailedIndexationInLibrary());
    const statusById = new Map(failed.data.flatMap(doc => (doc.indexationStatus ? [[doc.id, doc.indexationStatus] as const] : [])));
    const settling = new Set(ids);
    this.documents.update(list =>
      list.map(doc => (settling.has(doc.id) ? { ...doc, indexationStatus: statusById.get(doc.id) ?? 'success' } : doc))
    );
  }

  loadMore(): void {
    void this.load(false);
  }

  setQuery(value: string): void {
    this.query.set(value);
    void this.load(true);
  }

  toggleSubtype(id: number): void {
    // Single-select: click active chip to clear, otherwise replace selection.
    this.activeSubtypeIds.update(ids => (ids.length === 1 && ids[0] === id ? [] : [id]));
    void this.load(true);
  }

  clearSubtypes(): void {
    if (!this.activeSubtypeIds().length) return;
    this.activeSubtypeIds.set([]);
    void this.load(true);
  }

  setSort(field: LibrarySortField): void {
    if (this.sort() === field) return;
    this.sort.set(field);
    void this.load(true);
  }

  refresh(): void {
    this.clearImportState();
    void this.load(true);
    void this.loadSubtypeCounts();
    void this.loadUncategorized();
  }

  startImport(files: File[], rejectedFromZone: RejectedFile[] = []): void {
    const rejected: RejectedFile[] = [...rejectedFromZone];
    const accepted: File[] = [];

    for (const file of files) {
      const archive = isArchiveFile(file.name);
      const max = archive ? MAX_ARCHIVE_SIZE_BYTES : MAX_FILE_SIZE_BYTES;
      if (file.size > max) {
        rejected.push({
          name: file.name,
          reason: archive
            ? 'tenders.responses.documents.ignored-reason-too-large-archive'
            : 'tenders.responses.documents.ignored-reason-too-large',
        });
        continue;
      }
      accepted.push(file);
    }

    this.rejected.update(list => [...list, ...rejected]);
    if (!accepted.length) {
      if (rejected.length && this.importPhase() === 'idle') this.importPhase.set('done');
      return;
    }

    const existingNames = new Set(this.uploads().map(u => u.name.toLowerCase()));
    const fresh = accepted.filter(f => !existingNames.has(f.name.toLowerCase()));
    if (!fresh.length) return;

    const phase = this.importPhase();
    if (phase === 'uploading' || phase === 'analyzing' || this.showImportProgress()) {
      this.enqueueImport(fresh);
      return;
    }

    // Fresh session: drop resolved conflicts, keep unresolved ones.
    this.duplicates.update(list => list.filter(d => !d.resolution));
    this.showUncategorized.set(false);
    this.enqueueImport(fresh);
  }

  private enqueueImport(files: File[]): void {
    this.importChain = this.importChain.then(() => this.runImport(files)).catch(() => undefined);
  }

  async resolveDuplicate(item: DuplicateItem, resolution: DuplicateResolution): Promise<void> {
    if (item.resolution) return;
    this.duplicates.update(list => list.map(d => (d.uid === item.uid ? { ...d, resolution, phase: 'leaving' } : d)));

    if (item.conflictKey) {
      const pending = this.pendingWsConflicts.get(item.conflictKey);
      if (pending) pending.resolution = resolution;
      // Socket must stay open until processing_complete + all conflict acks.
      this.activeWsConn?.sendConflictResolution({
        action: resolution,
        key: item.conflictKey,
        document_id: resolution === 'replace' ? item.existingId : undefined,
        document_subtype_id: resolution === 'replace' ? item.existingSubtypeId : undefined,
      });
      if (resolution === 'replace') this.replacedCount.update(n => n + 1);
    } else if (resolution === 'replace' && item.file && item.existingId != null) {
      await this.replaceWithFile(item);
    }

    setTimeout(() => {
      this.duplicates.update(list => list.map(d => (d.uid === item.uid ? { ...d, phase: 'collapsing' } : d)));
    }, 1500);
    setTimeout(() => {
      this.duplicates.update(list => list.filter(d => d.uid !== item.uid));
      this.maybeMarkImportDone();
    }, 1800);
  }

  async updateCategory(doc: ResponseProjectDocument, subtype: ResponseDocumentSubtype, customName?: string): Promise<void> {
    try {
      await firstValueFrom(
        this.apiDocuments.updateDocument({
          id: Number(doc.id),
          response_document_subtype_id: subtype.id,
          custom_name: customName ?? undefined,
        })
      );
      this.documents.update(list =>
        list.map(d =>
          d.id === doc.id
            ? {
                ...d,
                responseDocumentSubtypeId: subtype.id,
                customName: customName ?? (subtype.name === SUBTYPE_OTHER_NAME ? d.customName : null),
              }
            : d
        )
      );
      if (subtype.name === 'undefined') {
        this.uncategorizedDocuments.update(list =>
          list.some(d => d.id === doc.id)
            ? list.map(d => (d.id === doc.id ? { ...d, responseDocumentSubtypeId: subtype.id, customName: customName ?? null } : d))
            : [...list, { ...doc, responseDocumentSubtypeId: subtype.id, customName: customName ?? null }]
        );
      } else {
        this.uncategorizedDocuments.update(list => list.filter(d => d.id !== doc.id));
        if (!this.uncategorizedDocuments().length) this.showUncategorized.set(false);
      }
      void this.loadSubtypeCounts();
      this.maybeMarkImportDone();
    } catch {
      // Keep previous category on failure.
    }
  }

  async deleteDocument(doc: ResponseProjectDocument): Promise<boolean> {
    try {
      await firstValueFrom(this.apiDocuments.deleteDocument(Number(doc.id)));
      this.documents.update(list => list.filter(d => d.id !== doc.id));
      this.uncategorizedDocuments.update(list => list.filter(d => d.id !== doc.id));
      this.totalCount.update(n => Math.max(0, n - 1));
      void this.loadSubtypeCounts();
      return true;
    } catch {
      return false;
    }
  }

  async downloadDocument(doc: ResponseProjectDocument): Promise<void> {
    if (this.downloadInFlight.has(doc.id)) return;
    this.downloadInFlight.add(doc.id);
    try {
      const fresh = await firstValueFrom(this.apiDocuments.getDocument(Number(doc.id)));
      const url = fresh.downloadPresignedUrl;
      if (!url) return;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fresh.name || doc.name;
      anchor.rel = 'noopener';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch {
      // Keep silent on failure; row menu already closed.
    } finally {
      this.downloadInFlight.delete(doc.id);
    }
  }

  subtypeOf(doc: ResponseProjectDocument): ResponseDocumentSubtype | undefined {
    return this.subtypeById().get(doc.responseDocumentSubtypeId);
  }

  subtypeDisplayName(doc: ResponseProjectDocument): string | null {
    const subtype = this.subtypeOf(doc);
    if (!subtype || subtype.name === 'undefined') return null;
    if (subtype.name === SUBTYPE_OTHER_NAME && doc.customName) return doc.customName;
    return subtype.displayedName;
  }

  autreSubtype(): ResponseDocumentSubtype | undefined {
    return this.subtypes().find(s => s.name === SUBTYPE_OTHER_NAME);
  }

  private async loadSubtypes(): Promise<void> {
    const result = await firstValueFrom(
      this.apiSubtypes.search({
        fields: ['id', 'name', 'displayed_name', 'scope'],
        filters: {},
        limit: 100,
        offset: 0,
      })
    );
    const internal = result.data.filter(s => s.scope === SCOPE_INTERNAL_DOC);
    this.subtypes.set(internal);
    this.defaultSubtypeId = internal.find(s => s.name === 'undefined')?.id;
    this.importContext.defaultSubtypeId = this.defaultSubtypeId;
  }

  private loadSubtypeCounts(): void {
    if (this.subtypeCountsTimer) clearTimeout(this.subtypeCountsTimer);
    this.subtypeCountsTimer = setTimeout(() => {
      this.subtypeCountsTimer = null;
      void this.fetchSubtypeCounts();
    }, SUBTYPE_COUNTS_DEBOUNCE_MS);
  }

  private async fetchSubtypeCounts(): Promise<void> {
    const subtypes = this.subtypes().filter(s => s.name !== 'undefined');
    if (!subtypes.length) {
      this.subtypeCounts.set({});
      return;
    }
    try {
      this.subtypeCounts.set(await firstValueFrom(this.apiDocuments.countLibraryBySubtype(subtypes.map(s => s.id))));
    } catch {
      // Keep previous counts on failure.
    }
  }

  async loadUncategorized(): Promise<void> {
    const undefinedId = this.defaultSubtypeId;
    if (undefinedId == null) {
      this.uncategorizedDocuments.set([]);
      return;
    }
    try {
      const result = await firstValueFrom(this.apiDocuments.findUncategorizedInLibrary(undefinedId));
      this.uncategorizedDocuments.set(this.withoutInFlight(result.data));
      if (!this.uncategorizedDocuments().length) this.showUncategorized.set(false);
    } catch {
      // Keep previous list on failure.
    }
  }

  private async runImport(files: File[]): Promise<void> {
    if (!files.length) return;

    this.importPhase.set('uploading');
    // A new batch may still need the socket after a prior processing_complete.
    this.wsProcessingComplete = false;

    const uploads: LibraryUploadItem[] = files.map(file => {
      const clientKey = `tmp-${++this.tempCounter}`;
      return {
        clientKey,
        uid: clientKey,
        name: file.name,
        size: file.size,
        phase: 'uploading' as const,
        progress: 0,
        sourceFile: file,
        isArchive: isArchiveFile(file.name),
      };
    });
    const batchKeys = new Set(uploads.map(u => u.clientKey));
    this.uploads.update(prev => [...prev, ...uploads]);

    const ctx = { ...this.importContext, defaultSubtypeId: this.defaultSubtypeId };
    const asUploadFiles: ResponseProjectUploadFile[] = uploads.map(u => ({
      uid: u.uid,
      name: u.name,
      size: u.size,
      sourceFile: u.sourceFile,
      processingStep: DocumentProcessingStepEnum.UPLOADING,
    }));
    const { archives, regular } = this.documentImport.splitArchives(asUploadFiles);
    const clientKeyByName = new Map(uploads.map(u => [u.name, u.clientKey]));

    const toUploadFirst = archives.length ? archives : regular;
    await this.documentImport.runWithConcurrency(toUploadFirst, UPLOAD_CONCURRENCY, f =>
      this.uploadOne(f, ctx, undefined, clientKeyByName.get(f.name))
    );

    const uploadedArchives = archives.filter(f => f.documentId != null);
    const uploadedRegular = regular.filter(f => f.documentId != null);
    const forWs = uploadedArchives.length ? uploadedArchives : uploadedRegular;

    if (!forWs.length) {
      this.syncImportPhaseFromUploads();
      return;
    }

    // Mark this batch as analyzing/extracting — leave other batches untouched.
    const archivedByName = new Map(uploadedArchives.map(a => [a.name, a]));
    this.uploads.update(list =>
      list.map(u => {
        if (!batchKeys.has(u.clientKey) || u.phase !== 'uploading') return u;
        const archived = archivedByName.get(u.name);
        if (archived) {
          return { ...u, uid: archived.uid, documentId: archived.documentId, phase: 'extracting', progress: 100 };
        }
        return { ...u, phase: 'analyzing', progress: 100 };
      })
    );
    this.syncImportPhaseFromUploads();

    const wsConn = this.ensureWs();
    this.sendWhenOpen(wsConn, this.documentImport.buildProcessingPayload(ctx, forWs), batchKeys);

    if (uploadedArchives.length && regular.length) {
      let downloadingCount = 0;
      let regularSent = false;
      const archiveSub = wsConn.messages$.pipe(filter(msg => msg.status === 'Downloading archive')).subscribe(() => {
        downloadingCount++;
        if (downloadingCount < uploadedArchives.length || regularSent) return;
        regularSent = true;
        void this.documentImport
          .runWithConcurrency(regular, UPLOAD_CONCURRENCY, f => this.uploadOne(f, ctx, undefined, clientKeyByName.get(f.name)))
          .then(() => {
            const uploaded = regular.filter(f => f.documentId != null);
            if (!uploaded.length) return;
            const uploadedByName = new Map(uploaded.map(a => [a.name, a]));
            this.uploads.update(list =>
              list.map(u => {
                if (!batchKeys.has(u.clientKey)) return u;
                const match = uploadedByName.get(u.name);
                return match ? { ...u, uid: match.uid, documentId: match.documentId, phase: 'analyzing', progress: 100 } : u;
              })
            );
            wsConn.sendPayload(this.documentImport.buildProcessingPayload(ctx, uploaded));
          });
      });
      this.wsSubs.push(archiveSub);
    }
  }

  private sendWhenOpen(wsConn: WsConnection, payload: ResponseProjectProcessingPayload, batchKeys: Set<string>): void {
    const openSub = wsConn.connectionState$.subscribe(state => {
      if (state === 'open') {
        wsConn.sendPayload(payload);
        openSub.unsubscribe();
      }
      if (state === 'error') {
        this.uploads.update(list => list.map(u => (batchKeys.has(u.clientKey) ? { ...u, phase: 'done' } : u)));
        this.syncImportPhaseFromUploads();
      }
    });
    this.wsSubs.push(openSub);
  }

  /** Keep overall phase coherent when several batches overlap. */
  private syncImportPhaseFromUploads(): void {
    const list = this.uploads().filter(u => !u.isArchive);
    if (list.some(u => u.phase === 'uploading')) {
      this.importPhase.set('uploading');
      return;
    }
    if (list.some(u => u.phase === 'analyzing' || u.phase === 'extracting')) {
      this.importPhase.set('analyzing');
      return;
    }
    this.maybeMarkImportDone();
  }

  private ensureWs(): WsConnection {
    if (this.activeWsConn) return this.activeWsConn;
    const wsConn = this.tendersResponses.connectToProcessing();
    this.activeWsConn = wsConn;
    this.bindWs(wsConn);
    return wsConn;
  }

  private uploadOne(file: ResponseProjectUploadFile, ctx: DocumentImportContext, replaceId?: number, clientKey?: string): Promise<void> {
    const key = clientKey;
    const matchesRow = (u: LibraryUploadItem) => (key ? u.clientKey === key : u.uid === file.uid || u.name === file.name);
    return new Promise(resolve => {
      this.documentImport.uploadFile(ctx, file, replaceId != null ? { replaceId } : undefined).subscribe(event => {
        switch (event.type) {
          case 'created':
            file.documentId = event.documentId;
            file.uid = event.uid;
            if (replaceId != null) this.silentReplaceUids.add(event.uid);
            this.uploads.update(list =>
              list.map(u => (matchesRow(u) && u.phase === 'uploading' ? { ...u, uid: event.uid, documentId: event.documentId } : u))
            );
            break;
          case 'progress':
            this.uploads.update(list => list.map(u => (matchesRow(u) ? { ...u, progress: event.progress } : u)));
            break;
          case 'uploaded':
            resolve();
            break;
          case 'error':
            if (event.reason === 'already-exists') {
              void this.resolveExistingDuplicate(file, key);
            } else {
              this.rejected.update(list => [
                ...list,
                {
                  name: file.name,
                  reason: 'tenders.responses.documents.ignored-reason-upload-error',
                },
              ]);
              this.uploads.update(list => list.filter(u => !matchesRow(u)));
            }
            resolve();
            break;
        }
      });
    });
  }

  private async replaceWithFile(item: DuplicateItem): Promise<void> {
    if (!item.file || item.existingId == null) {
      this.rejected.update(list => [...list, { name: item.name, reason: 'tenders.responses.documents.ignored-reason-replace-failed' }]);
      return;
    }
    const clientKey = `tmp-${++this.tempCounter}`;
    const file: ResponseProjectUploadFile = {
      uid: clientKey,
      name: item.name,
      size: item.file.size,
      sourceFile: item.file,
      replaceId: item.existingId,
      processingStep: DocumentProcessingStepEnum.UPLOADING,
    };
    const ctx = { ...this.importContext, defaultSubtypeId: item.existingSubtypeId ?? this.defaultSubtypeId };
    await this.uploadOne(file, ctx, item.existingId, clientKey);
    this.replacedCount.update(n => n + 1);
    this.maybeMarkImportDone();
  }

  private bindWs(wsConn: WsConnection): void {
    const msgSub = wsConn.messages$.subscribe(msg => {
      if (msg.status === 'extracting archive' || msg.status === 'Downloading archive' || msg.status === 'unzipping') {
        this.importPhase.set('analyzing');
        this.uploads.update(list => {
          if (!list.some(u => u.isArchive && u.phase !== 'extracting')) return list;
          return list.map(u => (u.isArchive ? { ...u, phase: 'extracting' } : u));
        });
      }

      // Archive contents counted — drop the parent zip row from the active list.
      if (msg.status === 'count') {
        this.uploads.update(list => list.map(u => (u.isArchive ? { ...u, phase: 'done' as const, uncategorized: false } : u)));
      }

      if (msg.status === 'uploading') {
        if (this.isSilentReplace(msg.uid, msg.filename)) return;
        this.uploads.update(list => {
          if (list.some(u => u.uid === msg.uid)) {
            return list.map(u => (u.uid === msg.uid ? { ...u, phase: 'analyzing', name: msg.filename || u.name } : u));
          }
          return [
            ...list,
            {
              clientKey: msg.uid,
              uid: msg.uid,
              name: msg.filename,
              phase: 'analyzing',
              progress: 100,
            },
          ];
        });
      }

      if (msg.status === 'uploaded') {
        const conflictKey = this.findWsConflictKeyByName(msg.filename, 'replace');
        if (conflictKey || this.silentReplaceUids.has(msg.uid)) {
          this.silentReplaceUids.add(msg.uid);
          this.uploads.update(list => list.filter(u => u.uid !== msg.uid));
          if (conflictKey) {
            this.pendingRefreshCount.update(n => n + 1);
            this.removeWsConflict(conflictKey);
          }
          return;
        }
        this.uploads.update(list => {
          if (list.some(u => u.uid === msg.uid)) {
            return list.map(u =>
              u.uid === msg.uid
                ? {
                    ...u,
                    documentId: msg.id,
                    size: msg.size,
                    name: msg.filename || u.name,
                    phase: 'analyzing' as const,
                    progress: 100,
                  }
                : u
            );
          }
          return [
            ...list,
            {
              clientKey: msg.uid,
              uid: msg.uid,
              name: msg.filename,
              documentId: msg.id,
              size: msg.size,
              phase: 'analyzing' as const,
              progress: 100,
            },
          ];
        });
      }

      if (msg.status === 'categorizing') {
        if (this.isSilentReplace(msg.uid)) return;
        this.uploads.update(list => list.map(u => (u.uid === msg.uid ? { ...u, phase: 'analyzing' } : u)));
      }

      if (msg.status === 'categorized') {
        if (this.silentReplaceUids.has(msg.uid)) {
          this.silentReplaceUids.delete(msg.uid);
          this.pendingRefreshCount.update(n => n + 1);
          return;
        }
        const subtype = this.subtypeById().get(msg.subtype_id);
        const isUndefined = !subtype || subtype.name === 'undefined';
        this.uploads.update(list =>
          list.map(u =>
            u.uid === msg.uid
              ? {
                  ...u,
                  phase: 'done',
                  subtypeId: subtype?.id ?? null,
                  customName: msg.custom_name ?? null,
                  uncategorized: isUndefined,
                }
              : u
          )
        );
        this.pendingRefreshCount.update(n => n + 1);
      }

      if (msg.status === 'processing_complete') {
        const byId = this.subtypeById();
        this.uploads.update(list =>
          list
            .filter(u => !u.isArchive)
            .map(u => {
              if (u.phase === 'done' || u.phase === 'error') return u;
              // A follow-up batch may still be uploading — leave it alone.
              if (u.phase === 'uploading') return u;
              const known = u.subtypeId != null && byId.get(u.subtypeId)?.name !== 'undefined';
              return {
                ...u,
                phase: 'done' as const,
                uncategorized: !known,
              };
            })
        );
        const stillActive = this.uploads().some(
          u => !u.isArchive && (u.phase === 'uploading' || u.phase === 'analyzing' || u.phase === 'extracting')
        );
        if (stillActive) {
          this.wsProcessingComplete = false;
          this.syncImportPhaseFromUploads();
          return;
        }
        this.wsProcessingComplete = true;
        this.maybeMarkImportDone();
        // Keep the socket open while zip name-conflicts still need keep/replace.
        this.maybeDisconnectWs();
      }

      if (msg.status === 'conflict_skipped') {
        this.removeWsConflict(msg.conflict_key);
        return;
      }

      if (msg.status === 'error') {
        if (msg.conflict_key && msg.reason !== WS_REASON_CONFLICT_EXPIRED) {
          void this.addWsConflict(msg.conflict_key, msg.filename ?? '');
          return;
        }
        if (msg.reason === WS_REASON_CONFLICT_EXPIRED && msg.conflict_key) {
          const name =
            this.pendingWsConflicts.get(msg.conflict_key)?.name ??
            this.duplicates().find(d => d.conflictKey === msg.conflict_key)?.name ??
            msg.filename ??
            '';
          if (name) {
            this.rejected.update(list => [...list, { name, reason: 'tenders.responses.documents.ignored-reason-conflict-expired' }]);
          }
          this.duplicates.update(list => list.filter(d => d.conflictKey !== msg.conflict_key || d.resolution));
          this.removeWsConflict(msg.conflict_key);
          return;
        }
        const translationKey = WS_ERROR_REASON_MAP[msg.reason] ?? WS_FALLBACK_ERROR_REASON;
        const name = msg.filename ?? '';
        if (name) this.rejected.update(list => [...list, { name, reason: translationKey }]);
        if (msg.uid) this.uploads.update(list => list.filter(u => u.uid !== msg.uid));
        this.maybeMarkImportDone();
      }
    });
    this.wsSubs.push(msgSub);
  }

  private async addWsConflict(conflictKey: string, name: string): Promise<void> {
    if (!conflictKey || !name) return;
    if (this.pendingWsConflicts.has(conflictKey)) return;
    this.pendingWsConflicts.set(conflictKey, { name });

    let existingId: number | undefined;
    let existingSubtypeId: number | undefined;
    let usedCount: number | undefined;
    let ownerName: string | null | undefined;
    const match = await this.lookupLibraryDocByName(name);
    if (match) {
      existingId = Number(match.id);
      existingSubtypeId = match.responseDocumentSubtypeId;
      usedCount = match.projects?.length ?? 0;
      ownerName = match.uploader?.name ?? null;
    }

    const existingDup = this.duplicates().find(d => d.conflictKey === conflictKey || (d.name === name && !d.resolution));
    if (existingDup) {
      existingDup.conflictKey = conflictKey;
      existingDup.existingId = existingId ?? existingDup.existingId;
      existingDup.existingSubtypeId = existingSubtypeId ?? existingDup.existingSubtypeId;
      if (usedCount != null) existingDup.usedCount = usedCount;
      if (ownerName !== undefined) existingDup.ownerName = ownerName;
      this.duplicates.update(list => [...list]);
      void this.finishImportSession();
      return;
    }

    this.addDuplicate({
      name,
      existingId,
      existingSubtypeId,
      conflictKey,
      usedCount,
      ownerName,
    });
  }

  private isSilentReplace(uid?: string, filename?: string): boolean {
    if (uid && this.silentReplaceUids.has(uid)) return true;
    return !!filename && !!this.findWsConflictKeyByName(filename, 'replace');
  }

  private findWsConflictKeyByName(name: string | undefined, resolution?: DuplicateResolution): string | undefined {
    if (!name) return undefined;
    for (const [key, entry] of this.pendingWsConflicts) {
      if (entry.name === name && (!resolution || entry.resolution === resolution)) return key;
    }
    return undefined;
  }

  private removeWsConflict(conflictKey: string): void {
    this.pendingWsConflicts.delete(conflictKey);
    this.maybeDisconnectWs();
  }

  private maybeDisconnectWs(): void {
    if (this.wsProcessingComplete && this.pendingWsConflicts.size === 0) {
      this.disconnectWs();
    }
  }

  private async resolveExistingDuplicate(file: ResponseProjectUploadFile, clientKey?: string): Promise<void> {
    this.uploads.update(list => list.filter(u => (clientKey ? u.clientKey !== clientKey : u.name !== file.name)));
    const incoming = file.sourceFile instanceof File ? file.sourceFile : undefined;
    const match = await this.lookupLibraryDocByName(file.name);
    this.addDuplicate({
      name: file.name,
      existingId: match ? Number(match.id) : undefined,
      existingSubtypeId: match?.responseDocumentSubtypeId,
      file: incoming,
      usedCount: match?.projects?.length ?? 0,
      ownerName: match?.uploader?.name ?? null,
    });
  }

  /** Exact-name library lookup with in-flight dedupe (ZIP conflicts often share names). */
  private lookupLibraryDocByName(name: string): Promise<ResponseProjectDocument | undefined> {
    let pending = this.libraryNameLookup.get(name);
    if (!pending) {
      pending = firstValueFrom(this.apiDocuments.findByNameInLibrary(name))
        .then(res => res.data.find(d => d.name === name))
        .catch(() => undefined);
      this.libraryNameLookup.set(name, pending);
    }
    return pending;
  }

  private addDuplicate(opts: {
    name: string;
    existingId?: number;
    existingSubtypeId?: number;
    file?: File;
    conflictKey?: string;
    usedCount?: number;
    ownerName?: string | null;
  }): void {
    if (!opts.name) return;
    if (this.duplicates().some(d => d.name === opts.name && !d.resolution)) return;
    this.duplicates.update(list => [
      ...list,
      {
        uid: `dup-${++this.duplicateCounter}`,
        name: opts.name,
        existingId: opts.existingId,
        existingSubtypeId: opts.existingSubtypeId,
        file: opts.file,
        conflictKey: opts.conflictKey,
        usedCount: opts.usedCount,
        ownerName: opts.ownerName,
      },
    ]);
    void this.finishImportSession();
  }

  private maybeMarkImportDone(): void {
    if (this.importPhase() === 'idle') return;
    const uploads = this.uploads().filter(u => !u.isArchive);
    const allUploadsSettled = uploads.length === 0 || uploads.every(u => u.phase === 'done' || u.phase === 'error');
    if (!allUploadsSettled) return;
    void this.finishImportSession();
  }

  private async finishImportSession(): Promise<void> {
    const epoch = this.importEpoch;
    await this.loadUncategorized();
    if (epoch !== this.importEpoch) return;
    this.importPhase.set('done');
  }

  private clearImportState(): void {
    this.importEpoch++;
    this.disconnectWs();
    this.wsProcessingComplete = false;
    this.pendingWsConflicts.clear();
    this.silentReplaceUids.clear();
    this.libraryNameLookup.clear();
    this.downloadInFlight.clear();
    this.importChain = Promise.resolve();
    this.importPhase.set('idle');
    this.uploads.set([]);
    this.duplicates.set([]);
    this.rejected.set([]);
    this.pendingRefreshCount.set(0);
    this.replacedCount.set(0);
    this.showUncategorized.set(false);
  }

  private disconnectWs(): void {
    this.wsSubs.forEach(s => s.unsubscribe());
    this.wsSubs = [];
    this.activeWsConn?.disconnect();
    this.activeWsConn = null;
  }

  private inFlightUids(): Set<string> {
    return new Set(
      this.uploads()
        .filter(u => u.phase === 'uploading' || u.phase === 'analyzing' || u.phase === 'extracting')
        .map(u => u.uid)
    );
  }

  private withoutInFlight(docs: ResponseProjectDocument[]): ResponseProjectDocument[] {
    const inflight = this.inFlightUids();
    if (!inflight.size) return docs;
    return docs.filter(d => !inflight.has(d.uid));
  }
}
